import axios from "axios";
import {errorHandler} from "../../Services/errorHandler";

/**
 * GINGER-20260629: Kazang V2 merchant-session 2FA actions (v1.2.0).
 *
 * <p>Per docs/kazang_v2_session_otp_spec_20260629.md: one OTP per 2-hour window
 * authorizes all deposits in that window. The sessionId lives server-side in
 * MerchantSessionService — we just call the 3 endpoints to manage its lifecycle.</p>
 *
 * <p>Wire to /api/v1/merchant-session/* — same base URL as the other Petty Cash
 * endpoints (uses the ticketApi2 constant declared in src/store/index.js).</p>
 */
export default {
    name: 'merchant_session',
    state: () => ({
        // Mirrors backend's SessionStatusResponse so any view can show the
        // current session state without re-fetching. Null = never checked.
        status: null,
        // The localId from the most recent /start call. Held while the OTP
        // modal is open; cleared on confirm or cancel.
        pendingSessionRequestId: null,
        // GINGER-20260630 (Jabulani): guard against double-firing /session/start.
        // If a start is already in flight, subsequent calls no-op until it resolves.
        isStarting: false,
        // GINGER-20260630 (Jabulani): survives component remounts across page
        // navigation. When the merchant clicks "Not now" on Dashboard.vue,
        // navigating to DeptManager.vue will not re-prompt them.
        dismissedSessionPrompt: false,
    }),
    getters: {
        isSessionActive: (state) => !!(state.status && state.status.active),
        sessionRemainingLabel: (state) =>
            (state.status && state.status.remainingLabel) || '0s',
        sessionPhase: (state) => (state.status && state.status.phase) || 'none',
        // True if the merchant clicked "Not now" on the "Start session" prompt.
        // SessionManager.vue reads this on mount to decide whether to re-prompt.
        wasPromptDismissed: (state) => state.dismissedSessionPrompt,
    },
    mutations: {
        setStatus(state, status) { state.status = status; },
        setPendingSessionRequestId(state, id) { state.pendingSessionRequestId = id; },
        setIsStarting(state, v) { state.isStarting = v; },
        setDismissedSessionPrompt(state, v) { state.dismissedSessionPrompt = v; },
    },
    actions: {
        /**
         * Step 1: start a session. Calls /api/v1/merchant-session/start.
         * Returns 202 + sessionRequestId envelope — caller must show OTP modal
         * and POST to confirmOtp.
         *
         * GINGER-20260630 (Jabulani): guarded by isStarting flag. If a start
         * is already in flight (rapid page nav, double-click), subsequent
         * calls no-op and return the in-flight sessionRequestId.
         */
        startMerchantSession: async (context, walletId) => {
            if (context.state.isStarting && context.state.pendingSessionRequestId) {
                // Already starting — return the in-flight request id instead of
                // firing another /session/create at Kazang.
                return { sessionRequestId: context.state.pendingSessionRequestId, deduped: true };
            }
            context.commit('setIsStarting', true);
            try {
                const { data } = await axios.post(
                    `${ticketApi2}merchant-session/start`,
                    null,
                    { params: { walletId } }
                );
                context.commit('setPendingSessionRequestId', data.sessionRequestId);
                return data;
            } finally {
                context.commit('setIsStarting', false);
            }
        },

        /**
         * Reset dismissal state. Called after a successful session start so
         * the prompt can re-appear when the 2-hour window expires.
         */
        clearSessionDismissal: async (context) => {
            context.commit('setDismissedSessionPrompt', false);
        },

        /**
         * Step 2: confirm the OTP. Calls /api/v1/merchant-session/confirm-otp.
         * On success, the backend caches the sessionId for 2 hours; we mirror
         * the result into the local state so SessionManager.vue can render the
         * active countdown.
         */
        confirmMerchantSessionOtp: async (context, payload) => {
            // payload = { walletId, sessionRequestId, otp }
            return axios.post(
                `${ticketApi2}merchant-session/confirm-otp`,
                null,
                { params: payload }
            ).then(({ data }) => {
                // Clear pending requestId; mirror the new status locally.
                context.commit('setPendingSessionRequestId', null);
                // GINGER-20260630: successful confirm = clear dismissal so the
                // prompt can re-appear when the 2-hour window expires.
                context.commit('setDismissedSessionPrompt', false);
                // Optimistic local status — full status is fetched by SessionManager's
                // /countdown poll, but we set active=true now so the UI flips immediately.
                context.commit('setStatus', {
                    phase: 'session',
                    active: true,
                    status: 'active',
                    sessionId: data.sessionId,
                    expiresAt: data.expiresAt,
                    remainingSeconds: 7200,  // 2hr default; real value comes from /countdown
                    remainingLabel: '2h 00m 00s',
                    message: data.message || 'Session active for 2 hours.',
                });
                return data;
            }).catch(err => errorHandler.tomcatError(err));
        },

        /**
         * Status check. Calls /api/v1/merchant-session/status. Pass remote=true
         * for the first call of the day to force a Kazang probe; subsequent
         * calls can use the local cache for sub-second latency.
         */
        getMerchantSessionStatus: async (context, { walletId, remote = false } = {}) => {
            return axios.get(
                `${ticketApi2}merchant-session/status`,
                { params: { walletId, remote } }
            ).then(({ data }) => {
                context.commit('setStatus', data);
                return data;
            }).catch(err => {
                // 401/404/empty wallet — silently no session. Don't spam the
                // user with errors on every page load.
                context.commit('setStatus', {
                    phase: 'none', active: false, status: 'none',
                    remainingSeconds: 0, remainingLabel: '0s',
                    message: 'No active session.',
                });
                return errorHandler.tomcatError(err);
            });
        },

        /**
         * Live countdown. Calls /api/v1/merchant-session/countdown. The backend
         * hits Kazang synchronously (per Grace's spec: "server-authoritative").
         * Frontend polls this 1x/sec while the user is in the OTP step or
         * active session.
         */
        getMerchantSessionCountdown: async (context, walletId) => {
            return axios.post(
                `${ticketApi2}merchant-session/countdown`,
                null,
                { params: { walletId } }
            ).then(({ data }) => {
                context.commit('setStatus', data);
                return data;
            }).catch(err => errorHandler.tomcatError(err));
        },

        /**
         * Convenience action: full start → OTP → confirm cycle, returning a
         * Promise that resolves on successful session activation. Used by
         * SessionManager.vue to drive the flow from a single button click.
         *
         * The OTP entry itself happens in the calling component (reuses
         * DepositOtpPrompt.show()). This action just bridges the two backend
         * calls.
         */
        ensureMerchantSession: async (context, { walletId, otpEntry }) => {
            // otpEntry: async () => Promise<string>  — caller passes a function that
            //   shows the OTP modal and returns the typed 6-digit code (or throws on cancel).
            if (!walletId) throw new Error('walletId is required');
            // Probe first — if already active, no need to OTP.
            const status = await context.dispatch('getMerchantSessionStatus', { walletId });
            if (status && status.active) return status;
            // Not active — start, prompt for OTP, confirm.
            const start = await context.dispatch('startMerchantSession', walletId);
            if (!start || !start.sessionRequestId) {
                throw new Error('startMerchantSession did not return sessionRequestId');
            }
            const otp = await otpEntry({ phoneHint: start.phoneHint, expiresAt: start.expiresAt });
            return context.dispatch('confirmMerchantSessionOtp', {
                walletId,
                sessionRequestId: start.sessionRequestId,
                otp,
            });
        },
    },
};
